import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Lock, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { scoreColor } from "./ProjectScoreCard";
import { Progress } from "@/components/ui/progress";

interface Props {
  projectId: string;
  isPro: boolean;
}

const RubricPanel = ({ projectId, isPro }: Props) => {
  const queryClient = useQueryClient();

  const { data: latest } = useQuery({
    queryKey: ["project-rubric", projectId],
    queryFn: async () => {
      const { data } = await supabase
        .from("project_scores")
        .select("*")
        .eq("project_id", projectId)
        .eq("score_type", "rubric")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      return data;
    },
  });

  const run = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("score-project", {
        body: { project_id: projectId, mode: "rubric" },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-rubric", projectId] });
      toast.success("Rubric scored.");
    },
    onError: (e: any) => toast.error(e?.message ?? "Rubric scoring failed"),
  });

  if (!isPro) {
    return (
      <div className="bg-gradient-to-br from-primary/10 to-card border border-primary/30 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-2">
          <Lock className="h-4 w-4 text-primary" />
          <h3 className="font-display text-lg font-semibold">Detailed Rubric</h3>
          <span className="text-[10px] uppercase tracking-wider bg-primary/20 text-primary px-2 py-0.5 rounded">Pro</span>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Get a 6-dimension breakdown — Problem clarity, Solution fit, Capacity, Budget realism, Outcomes, Sustainability — with per-dimension feedback and improvement actions.
        </p>
        <Button disabled className="gap-2">
          <Sparkles className="h-4 w-4" /> Coming soon
        </Button>
      </div>
    );
  }

  const rubricObj = (latest?.rubric ?? null) as { dimensions?: any[] } | null;
  const dims = rubricObj?.dimensions ?? [];

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <h3 className="font-display text-lg font-semibold">Detailed Rubric</h3>
        </div>
        <Button size="sm" variant="outline" onClick={() => run.mutate()} disabled={run.isPending} className="gap-2">
          {run.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          {latest ? "Re-run rubric" : "Run rubric"}
        </Button>
      </div>
      {!latest ? (
        <p className="text-sm text-muted-foreground">No rubric run yet.</p>
      ) : (
        <div className="space-y-4">
          <div className="flex items-baseline gap-3">
            <span className={`font-display text-3xl font-bold ${scoreColor(latest.score)}`}>{latest.score}</span>
            <span className="text-xs text-muted-foreground">overall</span>
          </div>
          <p className="text-sm text-muted-foreground">{latest.summary}</p>
          <div className="space-y-3 pt-2">
            {dims.map((d: any) => (
              <div key={d.name} className="border border-border rounded-lg p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm font-semibold">{d.name}</span>
                  <span className={`text-sm font-bold ${scoreColor(d.score)}`}>{d.score}</span>
                </div>
                <Progress value={d.score} className="h-1.5 mb-2" />
                <p className="text-xs text-muted-foreground mb-1.5">{d.feedback}</p>
                <p className="text-xs"><span className="text-primary font-semibold">Improvement:</span> <span className="text-muted-foreground">{d.improvement}</span></p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RubricPanel;